import { NgClass } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { AuthRoutes } from '@bn/auth/enums/auth-routes.enum';
import { ContentSlide } from '@bn/auth/models/sliders/content-slide.model';
import { AuthService } from '@bn/auth/services/auth.service';
import { AuthSliderService } from '@bn/auth/services/auth-slider.service';
import { EmailValidator, FormArrayValidator } from '@bn/primitives';
import { MatchValidator } from '@bn/primitives/validators/match.validator';
import { LazyLoadDirective, MarkAllTouchedDirective } from '@bn/web-ui';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { InputAction } from '@bn/web-ui/input/enums';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { InputOptions } from '@bn/web-ui/input/models';

import { ContentSliderComponent } from '../../components/content-slider/content-slider.component';

@Component({
  selector: 'bn-auth-dashboard-reset-password',
  templateUrl: './reset-password.page.html',
  host: { class: 'bn-auth__page-container' },
  imports: [
    ReactiveFormsModule,
    RouterLink,
    NgClass,
    FaIconComponent,
    ButtonComponent,
    ContentSliderComponent,
    InputComponent,
    MarkAllTouchedDirective,
    LazyLoadDirective,
  ],
})
export class ResetPasswordPage {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private readonly authService = inject(AuthService);
  private readonly authSliderService = inject(AuthSliderService);

  public sliderConfig?: ContentSlide[];
  public isLoading = {
    initial: true,
    submit: false,
  };
  public confirmationCode?: string;
  public mailSent = false;
  public form = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.email, EmailValidator.emailDomain()]),
    password: new UntypedFormControl({ value: '', disabled: true }, [Validators.required, FormArrayValidator.passwordPatternValidator()]),
    confirmPassword: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
  });

  public readonly passwordInputSettings: Partial<InputOptions> = {
    rightAddons: [
      {
        icon: 'eye',
        action: InputAction.ShowPassword,
      },
    ],
  };

  constructor() {
    this.route.queryParams.subscribe((params) => {
      if (params.code) {
        this.confirmationCode = (params.code as string).replace(/ /g, '+');
        this.form.controls.password.enable();
        this.form.controls.confirmPassword.enable();

        this.setValidators();
      }
    });

    this.getSlides();
  }

  public onSubmit(): void {
    this.isLoading.submit = true;

    if (!this.form.valid) {
      this.isLoading.submit = false;
      return;
    }

    if (this.confirmationCode) {
      this.authService.resetPasswordConfirm(this.form.get('email')?.value, this.form.get('password')?.value, this.confirmationCode).subscribe({
        next: () => {
          this.router.navigateByUrl(`/${AuthRoutes.Auth}/${AuthRoutes.Login}`);
        },
        error: () => {
          this.isLoading.submit = false;
        },
      });

      return;
    }

    this.authService.resetPassword(this.form.get('email')?.value, `${window.location.origin}/${AuthRoutes.Auth}/${AuthRoutes.ResetPassword}`).subscribe({
      next: () => {
        this.mailSent = true;
        this.isLoading.submit = false;
      },
      error: () => {
        this.isLoading.submit = false;
      },
    });
  }

  private setValidators(): void {
    if (!this.confirmationCode) return;

    this.form.setValidators([MatchValidator.match('password', 'confirmPassword')]);
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private getSlides(): void {
    this.authSliderService.getResetPasswordSlides().subscribe({
      next: (sliderConfig: ContentSlide[]) => {
        this.sliderConfig = sliderConfig;
        this.isLoading.initial = false;
      },
      error: () => (this.isLoading.initial = false),
    });
  }
}
